import * as XLSX from "xlsx";
import dayjs from "dayjs";
import {
  formatInUlaanbaatar,
  monthKeyInUlaanbaatar,
} from "../utils/ulaanbaatarTime";

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const statusLabel = (status) =>
  status === "PAID" ? "Төлөгдсөн" : "Төлөөгүй";

/** Нягтлан — нэхэмжлэлийн нэг мөр */
export function toInvoiceExcelRow(row, index) {
  return {
    No: index,
    ID: row.id,
    "Үүссэн огноо": formatInUlaanbaatar(row.createdAt),
    Мерчант: row.merchantName || "",
    "Мерчант ID": row.merchantId || "",
    Дүн: num(row.amount),
    Шимтгэл: num(row.feeAmount),
    "Нийт дүн": num(row.totalAmount),
    Төлөв: statusLabel(row.status),
    "Төлсөн хэлбэр": row.paymentMethod || "",
    Утас: row.phone || "",
    Тайлбар: row.description || "",
    "Мерчант дуудалт": row.callBackResponse || "",
    "Item тоо": row.invoiceItemCount ?? "",
  };
}

/** Нягтлан — invoice item мөр */
export function toInvoiceItemExcelRow(item, index) {
  return {
    No: index,
    "Invoice ID": item.invoiceId,
    "Item ID": item.id,
    "Үүссэн огноо": formatInUlaanbaatar(item.createdAt),
    Данс: item.accountNumber || "",
    Дүн: num(item.amount),
    Төлөв: item.status || "",
    "Бүтээгдэхүүн": item.productType || "",
    "Гүйлгээ ID": item.transactionId || "",
    "Гүйлгээ төлөв": item.transactionStatus || "",
    НӨАТ: item.vatStatus || "",
    Тайлбар: item.description || "",
    Банк: item.bankCode || "",
    Callback: item.callBackResponse || "",
  };
}

function monthKey(row) {
  return monthKeyInUlaanbaatar(row.createdAt);
}

function sheetTotals(rows) {
  let amount = 0;
  let fee = 0;
  let paid = 0;
  rows.forEach((r) => {
    amount += num(r.amount);
    fee += num(r.feeAmount);
    if (r.status === "PAID") paid += 1;
  });
  return {
    amount,
    fee,
    count: rows.length,
    paid,
    unpaid: rows.length - paid,
  };
}

/**
 * Тойм + сараар нэхэмжлэл sheet + InvoiceItems sheet.
 */
export function buildAdminInvoiceWorkbook(payload) {
  const {
    startDate,
    endDate,
    exportedAt,
    timezone,
    rows = [],
    summary = {},
  } = payload;

  const byMonth = new Map();
  rows.forEach((row) => {
    const key = monthKey(row);
    if (!byMonth.has(key)) byMonth.set(key, []);
    byMonth.get(key).push(row);
  });

  const monthKeys = [...byMonth.keys()].sort();
  const workbook = XLSX.utils.book_new();

  const summaryRows = [
    { Талбар: "Эхлэх огноо", Утга: startDate },
    { Талбар: "Дуусах огноо", Утга: endDate },
    {
      Талбар: "Цагийн бүс",
      Утга: timezone || "Asia/Ulaanbaatar (UTC+8)",
    },
    {
      Талбар: "Экспорт хийсэн",
      Утга: exportedAt || dayjs().format("YYYY-MM-DD HH:mm:ss"),
    },
    { Талбар: "Нийт мөр", Утга: rows.length },
    { Талбар: "Нийт дүн", Утга: summary.totalAmount ?? "" },
    { Талбар: "Нийт шимтгэл", Утга: summary.feeAmount ?? "" },
    { Талбар: "Төлөгдсөн", Утга: summary.paidCount ?? "" },
    { Талбар: "Төлөөгүй", Утга: summary.unpaidCount ?? "" },
    { Талбар: "", Утга: "" },
    { Талбар: "Сар", Утга: "Мөр / Дүн / Төлөгдсөн" },
  ];

  monthKeys.forEach((mk) => {
    const t = sheetTotals(byMonth.get(mk));
    summaryRows.push({
      Талбар: mk,
      Утга: `${t.count} мөр | Дүн: ${t.amount.toFixed(2)} | Төлөгдсөн: ${t.paid}`,
    });
  });

  XLSX.utils.book_append_sheet(
    workbook,
    XLSX.utils.json_to_sheet(summaryRows),
    "Тойм"
  );

  monthKeys.forEach((mk) => {
    const monthRows = byMonth.get(mk);
    const excelRows = monthRows.map((r, i) => toInvoiceExcelRow(r, i + 1));
    const t = sheetTotals(monthRows);
    excelRows.push({
      No: "",
      ID: "",
      "Үүссэн огноо": "",
      Мерчант: "НИЙТ",
      "Мерчант ID": "",
      Дүн: Number(t.amount.toFixed(2)),
      Шимтгэл: Number(t.fee.toFixed(2)),
      "Нийт дүн": "",
      Төлөв: `${t.paid} төлөгдсөн / ${t.unpaid} төлөөгүй`,
      "Төлсөн хэлбэр": "",
      Утас: "",
      Тайлбар: "",
      "Мерчант дуудалт": "",
      "Item тоо": t.count,
    });

    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(excelRows),
      mk.slice(0, 31)
    );
  });

  const itemRows = [];
  rows.forEach((inv) => {
    (inv.invoiceItems || []).forEach((item) => {
      itemRows.push(item);
    });
  });
  if (itemRows.length) {
    const itemExcel = itemRows.map((item, i) =>
      toInvoiceItemExcelRow(item, i + 1)
    );
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(itemExcel),
      "InvoiceItems"
    );
  }

  return workbook;
}

export function downloadAdminInvoiceExcel(payload) {
  const workbook = buildAdminInvoiceWorkbook(payload);
  const fileName = `Invoices_${payload.startDate}_${payload.endDate}.xlsx`;
  XLSX.writeFile(workbook, fileName);
}
